import { useCallback, useEffect, useState } from 'react'
import Boton from '../components/Boton'
import { fidelizacionApi } from '../api/fidelizacion'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { fmtFecha, fmtFechaHora, fmtMonto } from '../utils/fecha'
import { colors, sx, text, space, radius } from '../design/tokens'

// Estado de la presa → color + etiqueta. "Control" = elegida a propósito SIN
// mail (holdout del 10%), para medir la venta incremental real.
const ESTADOS = {
  enviado:   { label: 'Enviado',   bg: colors.okBg,   fg: colors.ok },
  holdout:   { label: 'Control',   bg: colors.infoBg, fg: colors.info },
  propuesto: { label: 'Elegida',   bg: colors.warnBg, fg: colors.warn },
  fallido:   { label: 'Falló',     bg: colors.errBg,  fg: colors.err },
}

function EstadoBadge({ estado }) {
  const e = ESTADOS[estado] || { label: estado || '—', bg: colors.bgSoft, fg: colors.textMeta }
  return <span style={{ ...sx.badge, background: e.bg, color: e.fg }}>{e.label}</span>
}

const monto = (n) => '$' + fmtMonto(n)

export default function CazadorPage() {
  const [tandas, setTandas] = useState([])
  const [fecha, setFecha] = useState('')          // '' = la última tanda
  const [presas, setPresas] = useState([])
  const [fechaCargada, setFechaCargada] = useState(null)
  const [loading, setLoading] = useState(true)
  const [detalle, setDetalle] = useState(null)

  const cargar = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true)
    try {
      const [t, p] = await Promise.all([
        fidelizacionApi.tandas(),
        fidelizacionApi.presas(fecha ? { fecha } : {}),
      ])
      setTandas(t.data || [])
      setPresas(p.data?.presas || [])
      setFechaCargada(p.data?.fecha || null)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [fecha])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(() => cargar({ silent: true }), 30000)

  // El detalle (hipótesis completa + mail HTML) se pide al abrir.
  async function abrirDetalle(row) {
    setDetalle(row)
    try {
      const { data } = await fidelizacionApi.presa(row.id)
      setDetalle(data)
    } catch { /* queda el resumen de la fila */ }
  }

  const tanda = tandas.find((t) => t.fecha?.slice(0, 10) === (fechaCargada || '').slice(0, 10))

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: space.lg, marginBottom: space.xl }}>
        <h2 style={{ margin: 0, fontSize: 20, color: colors.text }}>Cazador de fidelización</h2>
        <span style={{ fontSize: text.sec, color: colors.textSec }}>
          Los mails 1:1 que Opus elige y escribe cada día, y qué pasó con cada uno
        </span>
      </div>

      <div style={{ display: 'flex', gap: space.md, alignItems: 'center', marginBottom: space.lg, flexWrap: 'wrap' }}>
        <select
          value={fecha || (fechaCargada || '').slice(0, 10)}
          onChange={(e) => setFecha(e.target.value)}
          style={{ ...sx.input, color: colors.text }}
        >
          {tandas.map((t) => (
            <option key={t.fecha} value={t.fecha.slice(0, 10)}>
              Tanda {fmtFecha(t.fecha)} — {t.presas} presas
            </option>
          ))}
        </select>

        {tanda && (
          <span style={{ fontSize: text.sec, color: colors.textSec }}>
            📬 {tanda.enviados} enviados · 🔬 {tanda.control} control
            {Number(tanda.fallidos) > 0 && <> · <span style={{ color: colors.err }}>{tanda.fallidos} fallidos</span></>}
            {Number(tanda.compras) > 0 && <> · 💰 {tanda.compras} {Number(tanda.compras) === 1 ? 'compra' : 'compras'} ({monto(tanda.monto)})</>}
          </span>
        )}

        <div style={{ marginLeft: 'auto' }}>
          <Boton variante="ghost" sm onClick={() => cargar()}>↻ Actualizar</Boton>
        </div>
      </div>

      {loading ? (
        <p style={{ color: colors.textSec }}>Cargando…</p>
      ) : presas.length === 0 ? (
        <p style={{ color: colors.textSec }}>Todavía no hay ninguna tanda. La caza corre todos los días a las 10:15.</p>
      ) : (
        <table style={sx.table}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
              {['Enviado', 'Cliente', 'Asunto', 'Oferta', 'Estado', 'Resultado'].map((h) => (
                <th key={h} style={{ ...sx.th, textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {presas.map((r) => (
              <tr
                key={r.id}
                onClick={() => abrirDetalle(r)}
                title="Ver la jugada y el mail"
                style={{ borderBottom: `1px solid ${colors.borderSoft}`, cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgSoft }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '' }}
              >
                <td style={{ padding: space.md, ...sx.mono, color: colors.textSec, whiteSpace: 'nowrap' }}>
                  {r.enviado_at
                    ? fmtFechaHora(r.enviado_at)
                    : r.estado === 'propuesto' && r.enviar_a
                      ? <span style={{ color: colors.textMeta }}>⏰ sale {fmtFechaHora(r.enviar_a)}</span>
                      : '—'}
                </td>
                <td style={{ padding: space.md }}>
                  <div style={{ color: colors.text }}>{r.cliente || '—'}</div>
                  <div style={{ ...sx.mono, fontSize: text.sec, color: colors.textMeta }}>{r.email}</div>
                </td>
                <td style={{ padding: space.md, color: colors.textSec, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.asunto || '—'}
                </td>
                <td style={{ padding: space.md, color: colors.textSec, whiteSpace: 'nowrap' }}>
                  {r.items > 0 ? `${r.items} ${r.items === 1 ? 'producto' : 'productos'}` : (r.destino || '—')}
                </td>
                <td style={{ padding: space.md }}>
                  <EstadoBadge estado={r.estado} />
                  {r.error && <div style={{ fontSize: text.meta, color: colors.err, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.error}>{r.error}</div>}
                </td>
                <td style={{ padding: space.md, whiteSpace: 'nowrap', color: colors.textSec }}>
                  {r.convertido_at
                    ? <span style={{ color: colors.ok, fontWeight: 600 }}>💰 Compró {r.monto_atribuido ? monto(r.monto_atribuido) : ''}</span>
                    : r.click ? '🖱 Hizo click'
                    : r.abierto ? '👁 Abierto'
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detalle && (
        <ModalPresa
          presa={detalle}
          onClose={() => setDetalle(null)}
          onDescartada={async () => {
            const { data } = await fidelizacionApi.presa(detalle.id)
            setDetalle(data)
            cargar({ silent: true })
          }}
        />
      )}
    </div>
  )
}

// La jugada (hipótesis + oferta) y el mail tal cual lo recibió el cliente.
// El overlay NO cierra al click (regla de la casa): ✕ o Escape.
function ModalPresa({ presa, onClose, onDescartada }) {
  const [vista, setVista] = useState('jugada')
  const [descartando, setDescartando] = useState(false)

  // La atribución es por cercanía en el tiempo y puede leer mal (una compra
  // que vino por otro canal): este botón la corrige a mano.
  async function descartar() {
    if (!window.confirm('¿Descartar esta venta de la atribución? La compra queda registrada en Ventas; solo deja de contarse como resultado de este mail.')) return
    setDescartando(true)
    try {
      await fidelizacionApi.descartarAtribucion(presa.id)
      await onDescartada?.()
    } finally {
      setDescartando(false)
    }
  }

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  const items = presa.oferta?.items || []

  return (
    <div style={{
      position: 'fixed', inset: 0, background: colors.overlay, display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: space.xl,
    }}>
      <div style={{
        background: colors.bg, borderRadius: radius.lg, width: '100%', maxWidth: 680,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: space.md, padding: `${space.lg}px ${space.xl}px`, borderBottom: `1px solid ${colors.borderSoft}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: text.title, fontWeight: 700, color: colors.text }}>{presa.cliente || presa.email}</div>
            <div style={{ ...sx.mono, fontSize: text.sec, color: colors.textMeta }}>
              {presa.email}{presa.codigo_presea ? ` · cód. ${presa.codigo_presea}` : ''}
            </div>
          </div>
          <EstadoBadge estado={presa.estado} />
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: colors.textSec }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: space.md, padding: `${space.md}px ${space.xl}px 0` }}>
          {/* Solapas subrayadas — mismo patrón que el modal de Correos */}
          {[['jugada', 'La jugada'], ['mail', 'El mail']].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setVista(k)}
              style={{
                padding: '6px 2px', marginBottom: -1, background: 'none', cursor: 'pointer',
                border: 'none', borderBottom: `2px solid ${vista === k ? colors.link : 'transparent'}`,
                color: vista === k ? colors.link : colors.textSec,
                fontSize: text.body, fontWeight: vista === k ? 600 : 400,
              }}
            >{label}</button>
          ))}
        </div>

        <div style={{ overflowY: 'auto', borderTop: `1px solid ${colors.borderSoft}` }}>
          {vista === 'jugada' ? (
            <div style={{ padding: space.xl }}>
              <div style={{ fontSize: text.meta, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textSec, fontWeight: 600, marginBottom: space.sm }}>
                Por qué este cliente hoy
              </div>
              <p style={{ margin: `0 0 ${space.xl}px`, fontSize: text.body, color: colors.text, lineHeight: 1.55 }}>
                {presa.hipotesis || 'Cargando…'}
              </p>

              {presa.asunto && (
                <div style={{ marginBottom: space.xl }}>
                  <div style={{ fontSize: text.meta, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textSec, fontWeight: 600, marginBottom: space.sm }}>
                    Asunto
                  </div>
                  <div style={{ fontSize: text.body, color: colors.text }}>{presa.asunto}</div>
                </div>
              )}

              {items.length > 0 && (
                <>
                  <div style={{ fontSize: text.meta, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textSec, fontWeight: 600, marginBottom: space.sm }}>
                    La oferta
                  </div>
                  {items.map((it) => (
                    <div key={it.sku} style={{ display: 'flex', gap: space.md, padding: `${space.md}px 0`, borderBottom: `1px solid ${colors.borderSoft}` }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: text.body, color: colors.text }}>{it.cantidad > 1 ? `${it.cantidad} × ` : ''}{it.nombre}</div>
                        <div style={{ fontSize: text.sec, color: colors.textMeta }}>{it.motivo}</div>
                      </div>
                      <div style={{ ...sx.mono, fontSize: text.body, color: colors.text, whiteSpace: 'nowrap' }}>{monto(it.precio)}</div>
                    </div>
                  ))}
                </>
              )}
              {items.length === 0 && presa.oferta?.destino && (
                <div style={{ fontSize: text.body, color: colors.textSec }}>Sin productos — el mail invita a {presa.oferta.destino}.</div>
              )}

              <div style={{ marginTop: space.xl, fontSize: text.sec, color: colors.textSec }}>
                {presa.enviado_at && <div>Enviado: <span style={sx.mono}>{fmtFechaHora(presa.enviado_at)}</span></div>}
                {presa.abierto_at && <div>Abierto: <span style={sx.mono}>{fmtFechaHora(presa.abierto_at)}</span></div>}
                {presa.click_at && <div>Click: <span style={sx.mono}>{fmtFechaHora(presa.click_at)}</span></div>}
                {presa.convertido_at && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: space.md, color: colors.ok, fontWeight: 600 }}>
                    <span>
                      Compró: <span style={sx.mono}>{fmtFechaHora(presa.convertido_at)}</span>
                      {presa.monto_atribuido ? ` · ${monto(presa.monto_atribuido)}` : ''}
                    </span>
                    {presa.nv_atribuida_id && (
                      <Boton variante="ghost" sm onClick={descartar} disabled={descartando}>
                        ✕ No fue por el mail
                      </Boton>
                    )}
                  </div>
                )}
                {presa.error && <div style={{ color: colors.err }}>Error del envío: {presa.error}</div>}
              </div>
            </div>
          ) : presa.cuerpo_html ? (
            <iframe
              title="Mail enviado"
              srcDoc={presa.cuerpo_html}
              sandbox=""
              style={{ width: '100%', height: '62vh', border: 'none', display: 'block', background: colors.bg }}
            />
          ) : (
            <p style={{ padding: space.xl, color: colors.textSec, fontSize: text.body }}>
              {presa.estado === 'holdout'
                ? 'Presa de control: se eligió a propósito sin mandarle mail, para medir la venta incremental.'
                : 'El mail todavía no se generó (se arma al momento del envío).'}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
